/**
 * The one place a verb commits. It always reads the same way: a sentence
 * saying what happens and what it touches, one quiet secondary, and one blue
 * verb on the right. A plan gate also offers a quiet Ask before the
 * secondary. Everything else lives in the header's More menu.
 *
 * The sentence can sit behind a small "i" on the left (`whyAs="info"`): it
 * opens on hover, on keyboard focus and on a tap, so the 380px sheet beside
 * the chat keeps its verbs on one row. A refusal (`error`) is always shown
 * inline, and a caller keeps the sentence inline whenever it says why the
 * primary cannot be pressed, since the person needs it to act.
 */
import { useId, useRef, useState, type ReactNode } from "react";
import { Btn, cx } from "./primitives.js";

export interface SheetVerb {
  label: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  /** The id of what says why it is disabled (`whyId`), for a screen reader. */
  describedBy?: string;
}

export interface SheetFootProps {
  /** What the primary does and what it touches; `<b>` for the part that matters. */
  why: ReactNode;
  /** A verb that did not go through: its refusal stands in for the sentence until the next try. */
  error?: string | null;
  /** A quiet verb before the secondary that only asks (the plan gate's "Ask @yagni"). */
  ask?: SheetVerb;
  secondary?: SheetVerb;
  primary?: SheetVerb;
  /** An id for the sentence, so a disabled verb can point at its reason. */
  whyId?: string;
  /** "inline" (the default) prints the sentence; "info" puts it behind an "i" on the left. */
  whyAs?: "inline" | "info";
}

/** The sentence behind an "i": a button that describes itself with it, shown on hover, focus or tap. */
function WhyInfo({ why, id }: { why: ReactNode; id: string }) {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [pinned, setPinned] = useState(false);
  // A press focuses the button too; only focus from the keyboard opens it.
  const pressed = useRef(false);
  const open = hovered || focused || pinned;
  const close = () => {
    setHovered(false);
    setFocused(false);
    setPinned(false);
  };
  return (
    <span
      className="sinfo"
      // A mouse hovering opens it; a tap is a click, so it never opens and
      // closes on the same touch.
      onPointerEnter={(event) => {
        if (event.pointerType === "mouse") setHovered(true);
      }}
      onPointerLeave={() => setHovered(false)}
    >
      <button
        type="button"
        className="sinfo-btn"
        aria-label="What this does"
        aria-describedby={id}
        aria-expanded={open}
        data-testid="sheet-foot-info"
        // Keyboard focus opens it; the focus a tap or a click gives does not,
        // so the click alone decides.
        onPointerDown={() => {
          pressed.current = true;
        }}
        onFocus={() => {
          setFocused(!pressed.current);
          pressed.current = false;
        }}
        onBlur={close}
        onClick={() => {
          pressed.current = false;
          setFocused(false);
          setPinned((was) => !was);
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape") close();
        }}
      >
        i
      </button>
      <span
        role="tooltip"
        id={id}
        className={cx("stip", open && "on")}
        data-testid="sheet-foot-why"
      >
        {why}
      </span>
    </span>
  );
}

export function SheetFoot({
  why,
  error,
  ask,
  secondary,
  primary,
  whyId,
  whyAs = "inline",
}: SheetFootProps) {
  const ownId = useId();
  const info = !error && whyAs === "info";
  return (
    <div className={cx("sfoot", info && "tip")}>
      {error ? (
        <span className="why err" role="alert" data-testid="sheet-foot-error">
          {error}
        </span>
      ) : info ? (
        <WhyInfo why={why} id={whyId ?? ownId} />
      ) : (
        <span className="why" id={whyId}>
          {why}
        </span>
      )}
      <span className="verbs">
        {ask && (
          <Btn variant="quiet" onClick={ask.onClick} disabled={ask.disabled}>
            {ask.label}
          </Btn>
        )}
        {secondary && (
          <Btn variant="quiet" onClick={secondary.onClick} disabled={secondary.disabled}>
            {secondary.label}
          </Btn>
        )}
        {primary && (
          <Btn
            variant="primary"
            onClick={primary.onClick}
            disabled={primary.disabled}
            aria-describedby={primary.describedBy}
          >
            {primary.label}
          </Btn>
        )}
      </span>
    </div>
  );
}
